import { useCallback, useState } from 'react'
import type { ReserveChildMenuInput } from '~/lib/graphql/graphql'
import type { ChildMenuId } from '~/lib/graphql/scalars'
import { elementToValue } from '~/lib/selectBox'
import { useAdultCount } from '../store/childMenu'
import { useSelectedChildMenus, useVisitorsCount } from '../store/stateMachine/context'
import { useChildMenus } from '../store/stateMachine/state'
import type { ReserveInputChildMenu } from '../store/stateMachine/state'

export function useChangeCount() {
  const visitorsCount = useVisitorsCount()
  const initAdultCount = useAdultCount()
  const selected = useSelectedChildMenus()
  const childMenus = useChildMenus()
  const [adultCount, setAdultCount] = useState(initAdultCount)
  const childCount = visitorsCount - adultCount
  const [selectedChildMenus, setSelectedChildMenus] = useState(selected)
  const changeAdults = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setAdultCount(elementToValue(e))
  }, [])
  const changeChildren = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const childCount = elementToValue(e)
      setAdultCount(visitorsCount - childCount)
      // 子供を0名に変更した場合は、選択されているメニューをすべてクリアする
      if (childCount === 0) {
        setSelectedChildMenus([])
      }
    },
    [visitorsCount]
  )
  const selectChildMenu = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>, childMenu: ReserveInputChildMenu) => {
      const value = elementToValue(e)
      setSelectedChildMenus((current) => {
        const removed = removeChildMenu(current, childMenu)
        // 0点を選択した場合は配列から削除し、それ以外は配列に追加する
        return value === 0
          ? removed
          : [...removed, { childMenuId: childMenu.childMenuId, count: value }]
      })
    },
    []
  )
  // 大人は必ず1名以上必要なので、0名を選択できないように
  const adultOptions = createOptions(visitorsCount, '名', 1)
  const childOptions = createOptions(visitorsCount, '名')
  return {
    adultCount,
    childCount,
    childMenus,
    selectedChildMenus,
    changeAdults,
    changeChildren,
    selectChildMenu,
    adultOptions,
    childOptions,
  }
}

function removeChildMenu(selected: ReserveChildMenuInput[], childMenu: ReserveInputChildMenu) {
  return selected.filter((v) => v.childMenuId !== childMenu.childMenuId)
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('removeChildMenu', () => {
    const selected = [
      { childMenuId: '1' as ChildMenuId, count: 1 },
      { childMenuId: '2' as ChildMenuId, count: 2 },
    ] as ReserveChildMenuInput[]
    const childMenu = { childMenuId: '1' as ChildMenuId } as ReserveInputChildMenu
    expect(removeChildMenu(selected, childMenu)).toEqual([{ childMenuId: '2', count: 2 }])
  })
}

export function createOptions(index: number, label: string, start: number = 0) {
  return new Array(index).fill(0).map((_, i) => {
    return `${i + start}${label}`
  })
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('createOptions', () => {
    expect(createOptions(3, '名')).toEqual(['0名', '1名', '2名'])
    expect(createOptions(3, '名', 1)).toEqual(['1名', '2名', '3名'])
  })
}
